// `ConvList` — Wave 5c.1.
//
// Thin app-layer wrapper around the DS-D `ConvList` composite. This is the
// canonical surface (no re-implementation): the DS owns the visuals + the
// listbox a11y semantics. We just translate the data envelope from the
// api-client (`ConversationListItem`) into the DS row shape and wire the
// account-status banner (US-065).
//
// Stories shipped: US-061 (rail), US-062 (row visual states — delegated to
// the DS `ConvRow`), US-063 (account filter — DS), US-064 (mode tabs — DS),
// US-065 (status banner — see `accountStatusBanner` slot), US-068 (source
// pill — DS), US-070 (default selection — `InboxPage` controls).

import { useMemo, type ReactNode } from 'react';

import {
  type ConversationListItem as ApiConversation,
  type LinkedinAccountRow,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  ConvList as DsConvList,
  type ConvListProps as DsConvListProps,
  type AccountSummary,
  type ConversationListItem as DsConversationListItem,
  type ConversationPendingAction,
  type ModeTabDefinition,
  type ModeTabValue,
} from '@barel/ui';

import { formatRelativeTime } from '@/lib/formatRelativeTime';
import { RefreshConversationsButton } from './RefreshConversationsButton';

export type InboxModeTabValue = ModeTabValue;

export interface InboxConvListProps {
  accounts: LinkedinAccountRow[];
  conversations: ApiConversation[];
  selectedAccountId: string | 'all';
  onSelectAccount: (next: string | 'all') => void;
  modeTab: InboxModeTabValue;
  onModeTabChange: (next: InboxModeTabValue) => void;
  modeTabCounts: Record<InboxModeTabValue, number>;
  /** Whether to render the off-target tab (any account opted in). */
  showOffTargetTab: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNavigateToAccounts: () => void;
}

function toDsAccount(account: LinkedinAccountRow): AccountSummary {
  return {
    id: account.id,
    name: account.name,
    initials: account.initials,
    color: account.color ?? undefined,
    mode: (account.aiMode ?? 'manual') as AccountSummary['mode'],
    status: (account.status ?? 'active') as AccountSummary['status'],
    statusInfo: account.statusInfo ?? undefined,
  };
}

function toPendingAction(conv: ApiConversation): ConversationPendingAction | undefined {
  if (!conv.pendingActionType) return undefined;
  return {
    type: conv.pendingActionType,
    label: conv.pendingActionLabel ?? '',
    icon: conv.pendingActionIcon ?? undefined,
    detail: conv.pendingActionDetail ?? undefined,
    reason: conv.pendingActionReason ?? undefined,
    autoCompleted: conv.pendingActionAutoCompleted,
  };
}

function toDsConversation(
  conv: ApiConversation,
  accountById: Map<string, LinkedinAccountRow>,
): DsConversationListItem {
  const account = accountById.get(conv.accountId);
  const time = formatRelativeTime(conv.lastMessageAt);
  return {
    id: conv.id,
    name: conv.contactName,
    initials: conv.contactInitials,
    avatarUrl: conv.contactAvatarUrl ?? undefined,
    preview: conv.preview,
    // Wave B2 — denormalised `primaryBusinessName` from the list envelope.
    // Falls back to empty string when the conv has no primary business yet
    // (no chip rendered by the DS row).
    biz: conv.primaryBusinessName ?? '',
    source: conv.source,
    accountId: conv.accountId,
    accountName: account?.name ?? conv.accountName,
    accountInitials: account?.initials ?? conv.accountInitials,
    accountColor: account?.color ?? conv.accountColor ?? undefined,
    time,
    unread: conv.unread,
    offTarget: conv.offTarget,
    offTargetReason: conv.offTargetReason ?? undefined,
    pendingAction: toPendingAction(conv),
    temperature: conv.temperature ?? undefined,
  };
}

/** Pick the account whose status drives the banner (US-065). */
function pickBannerAccount(
  accounts: LinkedinAccountRow[],
  selectedAccountId: string | 'all',
): LinkedinAccountRow | null {
  if (selectedAccountId !== 'all') {
    const acc = accounts.find((a) => a.id === selectedAccountId);
    if (!acc) return null;
    if (acc.status === 'active') return null;
    return acc;
  }
  // First errored / paused / limited account.
  return (
    accounts.find((a) => a.status === 'error') ??
    accounts.find((a) => a.status === 'paused') ??
    accounts.find((a) => a.status === 'limited') ??
    null
  );
}

export function InboxConvList(props: InboxConvListProps): ReactNode {
  const t = useT('inbox');
  const accountById = useMemo(
    () => new Map(props.accounts.map((a) => [a.id, a])),
    [props.accounts],
  );

  const dsAccounts: AccountSummary[] = useMemo(
    () => props.accounts.map(toDsAccount),
    [props.accounts],
  );
  const dsItems = useMemo<DsConversationListItem[]>(
    () => props.conversations.map((c) => toDsConversation(c, accountById)),
    [props.conversations, accountById],
  );

  const bannerAccount = pickBannerAccount(props.accounts, props.selectedAccountId);
  const accountStatusBanner = bannerAccount ? (
    <AccountStatusBannerSlot account={bannerAccount} onNavigate={props.onNavigateToAccounts} />
  ) : undefined;

  const modeTabs: ModeTabDefinition[] = useMemo(() => {
    const base: ModeTabDefinition[] = [
      {
        value: 'all',
        label: t('list.tabAll'),
        count: props.modeTabCounts.all,
      },
      {
        value: 'action',
        label: t('list.tabAction'),
        icon: '⏸',
        count: props.modeTabCounts.action,
      },
      {
        value: 'auto',
        label: t('list.tabAuto'),
        icon: '🤖',
        count: props.modeTabCounts.auto,
      },
      {
        value: 'review',
        label: t('list.tabReview'),
        icon: '👁',
        count: props.modeTabCounts.review,
      },
    ];
    if (props.showOffTargetTab) {
      base.push({
        value: 'offtarget',
        label: t('list.tabOffTarget'),
        icon: '🚫',
        count: props.modeTabCounts.offtarget,
      });
    }
    return base;
  }, [t, props.modeTabCounts, props.showOffTargetTab]);

  const labels: DsConvListProps['labels'] = {
    region: t('list.regionLabel'),
    accountFilterEyebrow: t('list.accountFilterEyebrow'),
    allAccountsLabel: t('list.filterAllAccounts'),
    listbox: t('list.regionLabel'),
    noResults: t('list.noResults'),
  };

  return (
    <DsConvList
      accounts={dsAccounts}
      selectedAccount={props.selectedAccountId}
      onAccountChange={props.onSelectAccount}
      modeTab={props.modeTab}
      onModeTabChange={props.onModeTabChange}
      modeTabs={modeTabs}
      accountStatusBanner={accountStatusBanner}
      headerActions={
        <RefreshConversationsButton
          accounts={props.accounts}
          selectedAccountId={props.selectedAccountId}
        />
      }
      items={dsItems}
      selectedId={props.selectedId}
      onSelect={props.onSelect}
      labels={labels}
    />
  );
}

interface BannerSlotProps {
  account: LinkedinAccountRow;
  onNavigate: () => void;
}

function AccountStatusBannerSlot({ account, onNavigate }: BannerSlotProps): ReactNode {
  const t = useT('inbox');
  const status = account.status ?? 'active';
  const tone = status === 'error' ? 'error' : status === 'limited' ? 'warning' : 'info';
  const titleKey =
    status === 'error'
      ? 'accountStatusBanner.errorTitle'
      : status === 'paused'
        ? 'accountStatusBanner.pausedTitle'
        : 'accountStatusBanner.limitedTitle';
  const bodyKey =
    status === 'error'
      ? 'accountStatusBanner.errorBody'
      : status === 'paused'
        ? 'accountStatusBanner.pausedBody'
        : 'accountStatusBanner.limitedBody';
  const ctaKey =
    status === 'error'
      ? 'accountStatusBanner.ctaReconnect'
      : status === 'paused'
        ? 'accountStatusBanner.ctaResume'
        : 'accountStatusBanner.ctaSeeAccount';

  return (
    <Banner
      tone={tone}
      data-testid="inbox-account-status-banner"
      data-account-id={account.id}
      data-account-status={status}
    >
      <div className="flex flex-col gap-1">
        <strong>{t(titleKey)}</strong>
        <span>{account.statusInfo || t(bodyKey)}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onNavigate}
          data-testid="inbox-account-status-cta"
        >
          {t(ctaKey)}
        </Button>
      </div>
    </Banner>
  );
}
